// Link de pago y etiquetas de una orden, sea cual sea el proveedor.
//
// Antes cada componente parseaba `mercadopagoPreference` por su cuenta (y el
// texto del botón decía "MercadoPago" aunque el pago fuera de otro proveedor).
// La API ahora manda `paymentLink` y `paymentProvider` directo; el parseo queda
// sólo como fallback para las órdenes anteriores a esa migración.

export const getPaymentLink = purchaseOrder => {
    if (!purchaseOrder) return null;
    if (purchaseOrder.paymentLink) return purchaseOrder.paymentLink;
    if (!purchaseOrder.mercadopagoPreference) return null;

    try {
        const preference = JSON.parse(purchaseOrder.mercadopagoPreference);
        return preference.init_point || null;
    } catch (error) {
        return null;
    }
};

// Cómo lo lee el comprador. Payway es nuestro procesador de tarjetas, no algo
// que el comprador elija: para él es pagar con tarjeta. (La tienda sí lo ve
// como Payway: es la cuenta que vinculó.)
export const providerLabel = provider => {
    if (provider === 'payway') return 'Tarjeta de crédito / débito';
    if (provider === 'mercadopago') return 'MercadoPago';
    return null;
};

export const payButtonText = provider => {
    if (provider === 'payway') return 'Pagar con tarjeta de crédito / débito';
    const label = providerLabel(provider);
    return label ? `Pagar con ${label}` : 'Pagar el pedido';
};

// Título y bajada de un medio de pago en el checkout. La descripción de los
// medios online (payment_options.description) nombra al procesador y repite
// el título, así que sólo se muestra en los offline.
export const paymentOptionText = option => {
    const label = providerLabel(option.provider);
    return label
        ? { title: label, subtitle: null }
        : { title: option.name, subtitle: option.description || null };
};

// "Forma de pago" de una orden ya creada. Misma regla que en el checkout: la
// descripción de un medio online nombra al procesador ("Pago con tarjeta
// (Payway)"), así que sólo se usa en los offline.
export const paymentMethodText = purchaseOrder => {
    const option = purchaseOrder.paymentOption || {};
    return (
        providerLabel(purchaseOrder.paymentProvider || option.provider) ||
        option.description ||
        option.name ||
        ''
    );
};

// El verde es de la marca de MercadoPago: sólo se aplica cuando el pago es de
// MercadoPago. El resto usa el color primario del theme.
export const payButtonSx = provider =>
    provider === 'mercadopago'
        ? { backgroundColor: '#00a650', '&:hover': { backgroundColor: '#008f45' } }
        : undefined;

export const isPaid = purchaseOrder =>
    Boolean(purchaseOrder && purchaseOrder.paymentStatus === 'approved');

// El link de Payway es nuestra propia página, /pagar/:id?t=<token>, y el `t`
// es lo único que autoriza a ver y cobrar el pedido. Con él el formulario de
// pago se puede montar en un diálogo sobre la videollamada en vez de navegar
// a la página (lo que desmontaría la llamada).
export const getPaywayAccessToken = purchaseOrder => {
    if (!purchaseOrder || purchaseOrder.paymentProvider !== 'payway') return null;
    const link = getPaymentLink(purchaseOrder);
    if (!link) return null;
    try {
        return new URL(link, window.location.origin).searchParams.get('t');
    } catch (error) {
        return null;
    }
};
